import React, { useState } from 'react';
import { 
  AlertTriangle, 
  AlertCircle, 
  TrendingUp, 
  X,
  ChevronRight,
  Clock,
  DollarSign,
  Activity,
  Target
} from 'lucide-react';
import { DashboardAlert } from '../../../shared/types/dashboard';

interface AlertsBarProps {
  alerts: DashboardAlert[];
  onAction: (alert: DashboardAlert) => Promise<void>;
  onDismiss?: (alertId: string) => void;
  maxVisible?: number;
}

export const AlertsBar: React.FC<AlertsBarProps> = ({ 
  alerts, 
  onAction, 
  onDismiss,
  maxVisible = 5 
}) => {
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null);
  const [processingAlert, setProcessingAlert] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const visibleAlerts = showAll ? alerts : alerts.slice(0, maxVisible);
  const hasMore = alerts.length > maxVisible;

  const getAlertIcon = (alert: DashboardAlert) => {
    switch (alert.severity) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4" />;
      case 'opportunity':
        return <TrendingUp className="h-4 w-4" />;
    }
  };

  const getAlertTypeIcon = (type: DashboardAlert['type']) => {
    switch (type) {
      case 'budget':
        return <DollarSign className="h-3 w-3" />;
      case 'performance':
        return <Activity className="h-3 w-3" />;
      case 'milestone':
        return <Target className="h-3 w-3" />;
      case 'delivery':
        return <AlertTriangle className="h-3 w-3" />;
      default:
        return <AlertCircle className="h-3 w-3" />;
    }
  };

  const getAlertColor = (severity: DashboardAlert['severity']) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800';
      case 'opportunity':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
    }
  };

  const getActionButtonColor = (severity: DashboardAlert['severity']) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-600 hover:bg-red-700 text-white';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700 text-white';
      case 'opportunity':
        return 'bg-green-600 hover:bg-green-700 text-white';
    }
  };

  const handleAction = async (alert: DashboardAlert) => {
    setProcessingAlert(alert.id);
    try {
      await onAction(alert);
      setExpandedAlert(null);
    } catch (error) {
      console.error('Failed to execute alert action:', error);
    } finally {
      setProcessingAlert(null);
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  if (alerts.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center justify-center text-gray-500 dark:text-gray-400">
          <Activity className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">All systems operational</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
      <div className="flex items-start gap-3 overflow-x-auto pb-2">
        {visibleAlerts.map((alert) => (
          <div
            key={alert.id}
            className={`
              flex-shrink-0 rounded-lg border px-3 py-2 cursor-pointer
              transition-all duration-200 hover:shadow-md
              ${getAlertColor(alert.severity)}
              ${expandedAlert === alert.id ? 'ring-2 ring-offset-2 ring-blue-500' : ''}
            `}
            onClick={() => setExpandedAlert(expandedAlert === alert.id ? null : alert.id)}
          >
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5">
                {getAlertIcon(alert)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs opacity-75">
                    {getAlertTypeIcon(alert.type)}
                  </span>
                  <span className="text-xs font-medium">
                    {alert.campaign?.name || alert.artist?.name || 'System'}
                  </span>
                  <span className="text-xs opacity-60">
                    <Clock className="h-3 w-3 inline mr-1" />
                    {formatTimestamp(alert.timestamp)}
                  </span>
                </div>
                <p className="text-sm font-medium line-clamp-2">
                  {alert.title}
                </p>
                
                {expandedAlert === alert.id && (
                  <div className="mt-3 pt-3 border-t border-current/10">
                    {alert.description && (
                      <p className="text-xs opacity-75 mb-3">
                        {alert.description}
                      </p>
                    )}
                    {alert.metric !== undefined && alert.threshold !== undefined && (
                      <div className="text-xs mb-3">
                        <span className="opacity-75">Current: </span>
                        <span className="font-semibold">{alert.metric}</span>
                        <span className="opacity-75"> / Threshold: </span>
                        <span className="font-semibold">{alert.threshold}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAction(alert);
                        }}
                        disabled={processingAlert === alert.id}
                        className={`
                          px-3 py-1 rounded text-xs font-medium
                          transition-colors duration-200
                          disabled:opacity-50 disabled:cursor-not-allowed
                          ${getActionButtonColor(alert.severity)}
                        `}
                      >
                        {processingAlert === alert.id ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-3 w-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          alert.action.label
                        )}
                      </button>
                      {onDismiss && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDismiss(alert.id);
                          }}
                          className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {hasMore && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            className="
              flex-shrink-0 rounded-lg border border-gray-300 dark:border-gray-600
              px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700
              transition-colors duration-200
            "
          >
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>+{alerts.length - maxVisible} more</span>
              <ChevronRight className="h-4 w-4" />
            </div>
          </button>
        )}
      </div>
      
      {showAll && hasMore && (
        <button
          onClick={() => setShowAll(false)}
          className="mt-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Show less
        </button>
      )}
    </div>
  );
};
